import { Appearance, DraftConfiguration, PrintArea, PrintType, ProductType } from '@/types';
import { isEmbroideryMode } from '@/utils/params';
import { ensureSizeMm } from '@/model/configuration/convertSize';
import { findSuitablePrintArea } from '@/model/configuration/findSuitablePrintArea';
import { getAdjustedScaleAndOffset } from '@/model/configuration/getAdjustedScaleAndOffset';
import { getIsAdultDesignConfiguration } from '@/model/configuration/getIsAdultDesignConfiguration';
import {
  isDraftDesignConfiguration,
  isDraftTextConfiguration,
} from '@/model/configuration/isDraftConfigurationType';
import { isRestrictedScaleBelowMinimumScale } from '@/model/configuration/isRestrictedScaleBelowMinimumScale';
import { isVectorDesign } from '@/model/design/isDesignType';
import { getIsDieCutStickerPrintType } from '@/model/printType/getIsDieCutStickerPrintType';
import { isDigital } from '@/model/printType/isDigital';
import { getIsAdultDesignsVerboten } from '@/model/productType/getIsAdultDesignsVerboten';
import { getIsSingleConfigurationAppearance } from '@/model/productType/getIsSingleConfigurationAppearance';
import { changePrintType } from './changePrintType';
import { resizeTextToScale } from './resizeTextToScale';

/**
 * @returns number[] - list of configuration ids that cannot be placed on the new product type
 */
export function moveConfigurationsToProductType(
  configurations: DraftConfiguration[],
  currentProductType: ProductType,
  nextProductType: ProductType,
  appearance: Appearance
) {
  const printAreaMap = new Map<DraftConfiguration, PrintArea>();
  const availablePrintTypesMap = new Map<DraftConfiguration, PrintType[]>();
  const printTypeMap = new Map<DraftConfiguration, PrintType>();
  const configurationsPerPrintArea = new Map<PrintArea, DraftConfiguration[]>();
  const adultDesignsVerboten = getIsAdultDesignsVerboten(nextProductType);

  const checkedConfigurations: DraftConfiguration[] = [];
  const removeConfigurationIds: number[] = [];

  const isSingleConfiguration = getIsSingleConfigurationAppearance(appearance);
  let hasFoundSingleConfiguration = false;
  configurations.forEach((configuration) => {
    const [printArea, printTypes] = findSuitablePrintArea(
      configuration,
      currentProductType,
      nextProductType,
      appearance
    );

    if (
      printArea &&
      isEmbroideryMode() &&
      isDraftDesignConfiguration(configuration) &&
      isVectorDesign(configuration.design) &&
      isRestrictedScaleBelowMinimumScale(configuration, nextProductType, printArea.id)
    ) {
      return removeConfigurationIds.push(configuration.cid);
    }

    if (
      !printArea ||
      printTypes.length === 0 || // configuration is not supported on product type
      (adultDesignsVerboten && getIsAdultDesignConfiguration(configuration)) || // configuration rating is not allowed on product type
      (isSingleConfiguration && hasFoundSingleConfiguration)
    ) {
      return removeConfigurationIds.push(configuration.cid);
    } else if (isSingleConfiguration) {
      hasFoundSingleConfiguration = true;
    }

    checkedConfigurations.push(configuration);
    printAreaMap.set(configuration, printArea);
    // use the current print type if available on the new print area or the best match
    availablePrintTypesMap.set(configuration, printTypes);
    printTypeMap.set(
      configuration,
      printTypes.find((pt) => pt.id === configuration.printType.id) || printTypes[0]
    );
    if (!configurationsPerPrintArea.has(printArea)) {
      configurationsPerPrintArea.set(printArea, []);
    }
    configurationsPerPrintArea.get(printArea)!.push(configuration);
  });

  // prefer equalized print types on all print areas
  configurationsPerPrintArea.forEach((configurationsOnPrintArea) => {
    const firstConfigWithDigitalPrintType = configurationsOnPrintArea.find((configuration) =>
      isDigital(printTypeMap.get(configuration)!)
    );

    // equalization is only necessary when we have mixed digital and non-digital print types
    // different foil types such as flex/flock can be mixed freely
    if (configurationsOnPrintArea.length > 1 && firstConfigWithDigitalPrintType) {
      // convert all non-digital print types to digital
      // it is not possible to have multiple digital print types on a print area so we can safely use the first
      const printType = printTypeMap.get(firstConfigWithDigitalPrintType)!;
      configurationsOnPrintArea.forEach((configuration) => {
        // do nothing if there is no common print type and accept mixed print types
        //  see equalizeOnAdd for further details
        if (availablePrintTypesMap.get(configuration)!.includes(printType)) {
          printTypeMap.set(configuration, printType);
        }
      });
    }
  });

  checkedConfigurations.forEach((configuration) => {
    const printArea = printAreaMap.get(configuration)!;
    const printType = printTypeMap.get(configuration)!;

    if (getIsDieCutStickerPrintType(printType)) {
      if (configuration.rotation) {
        // remove rotation from configuration
        configuration.rotation = 0;
      }

      if (
        isDraftDesignConfiguration(configuration) &&
        (configuration.filter || configuration.mask)
      ) {
        // remove filter and masks from configuration
        configuration.filter = undefined;
        configuration.mask = undefined;
        configuration.processedImage = undefined;
        configuration.processedSize = undefined;
        configuration.size = ensureSizeMm(configuration.design.size, configuration.printType);
      }
    }

    const [scale, offset] = getAdjustedScaleAndOffset(configuration, printArea, printType);
    if (isDraftTextConfiguration(configuration)) {
      resizeTextToScale(configuration, printArea, printType, scale);
    } else {
      configuration.scale = scale;
    }
    configuration.offset = offset;
    configuration.printArea = printArea;
    changePrintType(configuration, printType, false);
  });

  return removeConfigurationIds;
}
